import { browser } from '$app/environment';
import { goto } from '$app/navigation';
import { appMode, AppMode, getThemeSetting, stateCatalogLanguage, stateLanguageOptions, stateThemeSetting } from '@infinitro/config';
import { derived, get, writable } from 'svelte/store';
import { sendMessageState, State, subscribeMessageState } from './uiStore';
export var SiteLanguageCode;
(function (SiteLanguageCode) {
    SiteLanguageCode["AR"] = "AR";
    SiteLanguageCode["CS"] = "CS";
    SiteLanguageCode["DE"] = "DE";
    SiteLanguageCode["EN"] = "EN";
    SiteLanguageCode["ES"] = "ES";
    SiteLanguageCode["FI"] = "FI";
    SiteLanguageCode["FR"] = "FR";
    SiteLanguageCode["ID"] = "ID";
    SiteLanguageCode["IT"] = "IT";
    SiteLanguageCode["JA"] = "JA";
    SiteLanguageCode["KO"] = "KO";
    SiteLanguageCode["MS"] = "MS";
    SiteLanguageCode["NO"] = "NO";
    SiteLanguageCode["PT"] = "PT";
    SiteLanguageCode["RU"] = "RU";
    SiteLanguageCode["TR"] = "TR";
    SiteLanguageCode["TH"] = "TH";
    SiteLanguageCode["UK"] = "UK";
    SiteLanguageCode["VI"] = "VI";
    SiteLanguageCode["ZH"] = "ZH";
})(SiteLanguageCode || (SiteLanguageCode = {}));
const VITE_APP_NAME = import.meta.env.VITE_APP_NAME;
const LANGPATH_APPS = [AppMode.Public];
export const IS_SITE = VITE_APP_NAME &&
    (VITE_APP_NAME?.endsWith('/public') ||
        VITE_APP_NAME?.endsWith('/player') ||
        VITE_APP_NAME?.endsWith('/mod'));
export const languages = {
    ar: 'العربية',
    cs: 'Čeština',
    de: 'Deutsch',
    en: 'English',
    es: 'Español',
    fi: 'Suomi',
    fr: 'Français',
    id: 'Bahasa Indonesia',
    it: 'Italiano',
    ja: '日本語',
    ko: '한국어',
    ms: 'Bahasa Melayu',
    no: 'Norsk',
    pt: 'Português',
    ru: 'Русский',
    tr: 'Türkçe',
    th: 'ไทย',
    uk: 'Українська',
    vi: 'Tiếng Việt',
    zh: '中文'
};
const site_language_codes = Object.values(SiteLanguageCode);
const getSelectedLocale = () => {
    if (browser && localStorage) {
        try {
            const v = localStorage.getItem('selectedLang');
            if (v && THEME_LANGUAGES.includes(v))
                return v;
        }
        catch (e) {
            // ignore
        }
    }
    return null;
};
// Persists `selectedLang` from the language dropdown (direct hits / sitemap do not set it).
// Used when the URL has no /xx/ segment: preference only — the path stays unprefixed on purpose.
const saveSelectedLocale = (lang) => {
    if (browser && localStorage) {
        try {
            localStorage.setItem('selectedLang', lang);
        }
        catch (e) {
            // ignore
        }
    }
};
/** Clears saved dropdown language and resets `locale` to browser/theme default. */
export const clearSelectedLocale = () => {
    if (browser && localStorage) {
        try {
            localStorage.removeItem('selectedLang');
        }
        catch (e) {
            // ignore
        }
    }
    DEFAULT_LANGUAGE = ACCEPT_SITE_LANGUAGE;
    locale.set(DEFAULT_LANGUAGE);
};
const THEME_SETTING = getThemeSetting();
const BASE_LANG = THEME_SETTING.languageDefault;
const THEME_LANGUAGES = THEME_SETTING.languages;
const AUTO_ACCEPT_DEFAULT_LANGUAGES = THEME_SETTING.languagesAutoAccept ?? [
    THEME_SETTING.languageDefault
];
const GAME_CATALOG_LANGUAGES = THEME_SETTING.languagesGameCatalog ?? [
    THEME_SETTING.languageDefault
];
export const ACCEPT_LANGUAGE = (navigator?.language || BASE_LANG).split('-')[0];
export const ACCEPT_SITE_LANGUAGE = ACCEPT_LANGUAGE &&
    THEME_LANGUAGES.includes(ACCEPT_LANGUAGE) &&
    AUTO_ACCEPT_DEFAULT_LANGUAGES.includes(ACCEPT_LANGUAGE)
    ? ACCEPT_LANGUAGE
    : BASE_LANG;
// When the URL has no /xx/ segment, `getPathLocale` falls back to this (dropdown + localStorage).
// It is the "explicit UI language" for unprefixed routes; it does not imply a path prefix.
let DEFAULT_LANGUAGE = getSelectedLocale() ?? ACCEPT_SITE_LANGUAGE;
export const getPathLocale = (path) => {
    if (typeof window === 'undefined')
        return DEFAULT_LANGUAGE;
    path = path ?? new URL(window.location.href).pathname;
    const pathLang = path.match(/\/([a-zA-Z]{2})\//);
    // Prefixed URL (/es/…): language comes from the path. Unprefixed: from DEFAULT_LANGUAGE (dropdown / selectedLang).
    const lang = pathLang ? pathLang[1] : DEFAULT_LANGUAGE;
    return languages[lang] ? lang : DEFAULT_LANGUAGE;
};
export const getStrictPathLocale = (path) => {
    if (typeof window === 'undefined')
        return undefined;
    path = path ?? new URL(window.location.href).pathname;
    const pathLang = path.match(/\/([a-zA-Z]{2})(?:\/|$)/);
    const lang = pathLang ? pathLang[1] : null;
    if (!lang)
        return null;
    return languages[lang] ? lang : null;
};
/**
 * After each navigation: pathname is source of truth for `locale` / `pathLocale` / `selectedLocale`.
 * Crawlers and users on /es/… get UI language from the path. This does not add /xx/ to unprefixed URLs;
 * unprefixed + dropdown still uses preference-only behavior (see `selectedLocale` subscribe).
 */
export const syncLocaleFromPathname = (pathname) => {
    pathLocale.set(getStrictPathLocale(pathname));
    const next = getPathLocale(pathname);
    locale.set(next);
    selectedLocale.set(next);
};
export const gotoPathLocale = (lang) => {
    if (typeof window === 'undefined')
        return;
    let path = new URL(window.location.href).pathname;
    const pathLang = path.match(/\/([a-zA-Z]{2})\//);
    if (!LANGPATH_APPS.includes(appMode)) {
        if (pathLang)
            path = path.replace(pathLang[0], `/${lang}/`);
        goto(path);
        return;
    }
    if (pathLang) {
        path = path.replace(pathLang[0], `/${lang}/`);
    }
    else if (path.startsWith(`/${appMode}/`))
        path = path.replace(`/${appMode}/`, `/${appMode}/${lang}/`);
    else
        path = `/${lang}${path}`;
    // Use theme default (BASE_LANG), not DEFAULT_LANGUAGE: the subscribe callback sets
    // DEFAULT_LANGUAGE = lang before calling here, so lang === DEFAULT_LANGUAGE would
    // always strip — first direct hit on /xx/ would redirect to unprefixed URL.
    if (lang === BASE_LANG) {
        path = path.replace(`/${lang}/`, `/`);
        pathLocale.set(null);
    }
    else {
        pathLocale.set(lang);
    }
    const currentPath = new URL(window.location.href).pathname;
    if (path === currentPath)
        return;
    goto(path);
};
export const toSiteLanguageCode = (lang) => {
    if (!lang)
        return (DEFAULT_LANGUAGE?.toUpperCase() ?? SiteLanguageCode.EN);
    lang = lang.toUpperCase();
    return (site_language_codes.find((code) => code === lang) ??
        SiteLanguageCode.EN);
};
let _locale = getPathLocale();
export const locale = writable(_locale); // the current page locale (w/ or w/o language path)
export const defaultLocale = writable(DEFAULT_LANGUAGE); // the default locale of the site (w/o langauge path)
export const selectedLocale = writable(_locale); // the user selected locale via dropdown (but not via navigation)
export const pathLocale = writable(getStrictPathLocale()); // the locale in the url path
export const selectedLanguageOption = derived([locale, stateThemeSetting, stateLanguageOptions], ([$locale, $stateThemeSetting, $stateLanguageOptions]) => {
    const option = $stateLanguageOptions.find((option) => option.code == $locale);
    if (option)
        return option;
    locale.set($stateThemeSetting.languageDefault);
});
if (typeof window !== 'undefined') {
    subscribeMessageState((message) => {
        // console.log(`locale state message`, message);
        if (message.state === State.Language) {
            locale.set(message.value.toString());
        }
    });
}
locale.subscribe((lang) => {
    if (lang === _locale)
        return;
    console.log(`locale changed to ${lang}`);
    _locale = lang;
    if (GAME_CATALOG_LANGUAGES.includes(lang)) {
        stateCatalogLanguage.set(lang);
    }
    else {
        stateCatalogLanguage.set(get(stateThemeSetting).languageDefault);
    }
});
stateLanguageOptions.subscribe((options) => {
    if (!options.find((item) => item.code === _locale)) {
        locale.set(get(stateThemeSetting).languageDefault);
    }
});
let _selectedLocale = DEFAULT_LANGUAGE;
// Language dropdown → `selectedLocale`. Two behaviors on Public (product / SEO):
// 1) Unprefixed URL (/casino/, …): update locale + localStorage + defaultLocale only — do NOT call
//    gotoPathLocale, so the address bar stays without /xx/. Crawlers use sitemap / prefixed links;
//    logged-out users who want a shareable locale URL open /es/… directly.
// 2) Prefixed URL (/es/…): persist preference and gotoPathLocale so the path segment matches the pick.
selectedLocale.subscribe((lang) => {
    const pathLang = getStrictPathLocale();
    if (_selectedLocale === lang)
        return;
    _selectedLocale = lang;
    locale.set(_selectedLocale);
    if (!pathLang) {
        saveSelectedLocale(lang);
        defaultLocale.set(lang);
        DEFAULT_LANGUAGE = lang;
    }
    else if (LANGPATH_APPS.includes(appMode)) {
        // Public: URL already has /xx/ — swap segment so path matches language (SEO); also persist preference.
        saveSelectedLocale(lang);
        defaultLocale.set(lang);
        DEFAULT_LANGUAGE = lang;
        // Skip goto when URL segment already matches `lang` (initial subscribe would otherwise call
        // gotoPathLocale after DEFAULT_LANGUAGE was set, and default-lang strip logic removes /es/).
        if (getStrictPathLocale() !== lang)
            gotoPathLocale(lang);
    }
    else if (IS_SITE && !LANGPATH_APPS.includes(appMode)) {
        gotoPathLocale(lang);
    }
    sendMessageState({ state: State.Language, value: _selectedLocale });
});
